import SignupForm from "@/components/koach/Signup";
import Image from "next/image";
import Feature from "@/components/how-it-works/Feature";
import { ReactElement } from "react";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/KoachLayout";
import { handleSignUpClick } from "@/components/koach/Shared/Helper";
import SignupButtonComponent from "@/components/koach/Shared/SignupButtonComponent";

const HowItWorks = (): JSX.Element => {
  return (
    <>
      <Head>
        <title>How It Works - The App Helping You Buy A Home</title>
        <link rel="canonical" href="https://www.klink-app.com/how-it-works" />
        <meta
          name="description"
          content="Get started with Klink to earn rewards for spending less money. Find out how to get into your future home sooner."
        />

        <meta
          name="twitter:card"
          content="Get started with Klink to earn rewards for spending less money. Find out how to get into your future home sooner."
          key="twcard"
        />
        <meta
          name="twitter:url"
          content="https://www.klink-app.com/how-it-works"
        />
        <meta
          name="twitter:title"
          content="How It Works - The App Helping You Buy A Home"
        />
        <meta name="twitter:image" content="/meta-opengraph-how-it-works.png" />
        {/* TODO */}
        <meta name="twitter:creator" content="klink_app" key="twhandle" />

        <meta
          property="og:url"
          content="https://www.klink-app.com/how-it-works"
          key="ogurl"
        />
        <meta property="og:url" content="/" key="ogurl" />
        <meta
          property="og:image"
          content="/meta-opengraph-guide.png"
          key="ogimage"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Klink" key="ogsitename" />
        <meta
          property="og:title"
          content="How It Works - The App Helping You Buy A Home"
          key="ogtitle"
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Get started with Klink to earn rewards for spending less money. Find out how to get into your future home sooner."
          key="ogdesc"
        />
      </Head>

      <div className="md:pt-10 grid grid-cols-1 md:grid-cols-2 gap-1 md:gap-0 bg-navy w-full overflow-hidden text-white md:px-6 px-5 lg:px-11 xl:px-32">
        <div className="flex gap-2 md:gap-8 flex-col items-center md:items-start pt-10 z-20 md:mb-20">
          <h1 className="text-4xl lg:text-5xl 2xl:text-6xl font-bold text-center md:text-left text-landing-white leading-[1.1]">
            <span className="z-40 hidden md:inline-block bg-gradient-to-r from-magenta to-landing-orange bg-clip-text text-transparent">
              How It Works
            </span>
          </h1>
          <h3 className="block px-1 text-xl text-grey-200 text-left">
            Get started with Klink to earn rewards for spending less money.
            <br />
            Here’s how to get into your future home sooner...
          </h3>
          <SignupButtonComponent/>
        </div>

        <div className="bg-gray-200 flex items-end justify-center relative">
          <p></p>
        </div>
      </div>

      <div className="pb-10 md:pb-0 md:pt-10 grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-10 bg-navy w-full overflow-hidden text-white md:px-6 px-5 lg:px-11 xl:px-32 second-item-vertical-offset">
        <Feature
          title="Set your home buying goal"
          subtitle="Tell us a little about yourself and your home buying goals and we’ll show you how long it will take to get there. "
          imageSrc="set-your-home-buying-goal.png"
        />
        <Feature
          title="Learn what you can afford today"
          subtitle="See what your home buying budget is today to get an idea of how close you are to affording your ideal home."
          imageSrc="learn-what-you-can-afford-today.png"
        />
         <Feature
          title="Get detailed spending insights"
          subtitle="Link your bank statements through Open Banking and get detailed insights into how you spend your money. "
          imageSrc="get-detailed-spending-insights.png"
        />
        <Feature
          title="Get mortgage ready"
          subtitle="Ever wondered if you’ll be approved for a mortgage and why they might say no? Our readiness score will give you an idea of your mortgage chances. "
          imageSrc="get-mortgage-ready2.png"
        />
        <Feature
          title="Read about home buying and finances"
          subtitle="Make the most of our detailed home buying and financial wellbeing guides to get mortgage ready."
          imageSrc="read-about-home-buying-and-finances.png"
        />
        <Feature
          title="Have fun and earn Klink rewards"
          subtitle="Take fun quizzes, refer your friends, and use the app’s features to get Klink rewards."
          imageSrc="have-fun-and-earn-klink-rewards.png"
        />
        <Feature
          title="Win cash by competing against other users"
          subtitle="The more rewards you earn, the higher your chances of winning cash in our daily and weekly competitions."
          imageSrc="win-cash-by-competing-against-other-users.png"
        />
        <Feature
          title="Spend less to save more"
          subtitle="Accept challenges to spend less on certain categories to save more money and get rewarded by Klink."
          imageSrc="spend-less-to-save-more.png"
        />
       
      </div>
      <div className="py-16">
        <SignupForm />
      </div>
    </>
  );
};

HowItWorks.getLayout = function getLayout(page: ReactElement) {
  return (
    <>
      <MainLayout theme="dark">{page}</MainLayout>
    </>
  );
};

export default HowItWorks;
